---
title: Mailboxes
summary: "The Mailboxes page connects the inboxes and WhatsApp groups the business listens to, shows which one needs attention, and sets how each incoming message is routed."
visibility: authenticated
feature: email_ingestion
audience: operator
section: Pages
order: 60
last_verified: 2026-10-27
owner: docs
routes: ['/admin/mailboxes']
aliases: [email ingestion, inbound mail, group inbox, WhatsApp group source, routing policy, connect mailbox, shared inbox]
tour:
  - anchor: mailboxes.list
    text: "Every mailbox and WhatsApp group source, with its status. The line under a name says what needs attention first, such as a lost connection."
  - anchor: mailboxes.pane.header
    text: "The selected mailbox: its status, the account it reads and the everyday actions. Delete is in the ⋯ menu."
    when_missing: "Select a mailbox in the list to see this."
  - anchor: mailboxes.pane.routing
    text: "Routing decides what each incoming message is and what happens to it."
    when_missing: "Select a mailbox in the list to see this."
  - anchor: mailboxes.header.providers
    text: "Register each mail provider's sign-in client here before you connect a mailbox."
    when_missing: "No mail provider needs a sign-in client on this deployment."
  - anchor: mailboxes.header.new
    text: "Add a mailbox or a WhatsApp group source."
---

# Mailboxes

A mailbox is an inbox, or a WhatsApp group, that the app listens to. Each message that
arrives becomes a conversation in the Inbox, and the mailbox's routing says what else
happens to it: a record is made, a workflow starts, or a person decides. Open the page
from **Admin**, then **Mailboxes**. Email is inbound-only.

## The list

The list on the left holds every mailbox. Each row shows its name, a status chip and one
line. The line names the most urgent thing first: a lost connection, a problem with the
routing, mail that could not be processed, then what is waiting for a decision. A healthy
mailbox shows what it does instead. Press a row to open it in the pane beside the list.

## The pane

The top of the pane names the mailbox and shows its status, its provider, the account it
reads and when it was last polled. A banner appears only when something is wrong.

The everyday actions sit beside the name:

- **Connect** signs the mailbox in with its provider. It shows while the mailbox is not connected or needs signing in again.
- **Pause** stops the mailbox being read, and **Resume** starts it again.
- **Change account** moves the mailbox to another account's inbox. It keeps the mailbox's threads; mail that reached the new account earlier comes in only through **Load earlier mail**.
- **Poll now** reads the mailbox straight away instead of waiting for the next check.
- **Delete** is in the ⋯ menu. An email mailbox is removed from the app, not from the provider. A linked
  WhatsApp group source also logs the desk out of WhatsApp; a paused one leaves the phone linked until you
  remove it under Linked devices.

Below the header are tabs. **Overview** shows the connection, what the mailbox does, the
outbound setting and, on a mailbox that loads earlier mail, **Load earlier mail**, which
brings in older messages for a date range you choose. **Cancel load** stops one that is
running. **Routing** holds the routing policy. **Triage** appears on a mailbox that uses a
router and shows how its recent messages were sorted.

## Routing

A mailbox either sends every message to one place, or uses a router that decides, for
each message, what kind it is and what to do about it. A mailbox that is not a router
offers **Use a router**. A router lists the kinds it knows. Press **Edit routing** to
change them. **Add kind** opens a short form for a label, an id, a description and a
handler, and adds that one kind without touching the rest.

**Save routing** checks the policy and shows **What Save will change**; press **Save these
changes** to save it. A policy pasted from the other channel's mailbox is adjusted first:
a rule that names a condition this channel never matches is left out whole, and a kind
whose handler this channel does not offer is kept and set to "Do nothing more". The review
names each. If someone else saved the routing since your check, the save is refused and
the review comes back with the changes again. If you leave with unsaved changes, the page
asks first.

## New mailbox

**New mailbox** opens a form in the pane for a name, a provider and what the mailbox
does. Saving selects the new mailbox so you can connect it. A WhatsApp group source is
always a router: create it, then add its kinds under **Routing**.

## Provider settings

**Provider settings** holds the sign-in client each mail provider needs before a mailbox
can connect. A dot on the button means a provider has no client yet. Set these once per
deployment.

## Connecting

Press **Connect** on an email mailbox. The provider's sign-in page opens, and when you finish you
come back to this page with the mailbox selected. If the account you signed in with is not
the one the mailbox expects, the page says so and nothing is stored.

A mailbox can use a shared address that customers write to while a different account reads it. Sign in
as the account whose inbox receives that address; if you signed in as another account, the page offers to
connect as the account you signed in with. A mailbox marked "Reads a different account" can be accepted as it
is with **Confirm**.

On a WhatsApp group source, **Connect** opens the linking dialog, "Link the desk's WhatsApp", on
this page: link the desk's phone with a code or a QR code, then choose the group to listen to.
